/**
 * Dashboard Container - Contenedor principal del dashboard
 *
 * Renderiza un grid responsive de widgets.
 */

import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Grid, Skeleton, Alert, Button } from '@mui/material';
import { TuneIcon } from "../../../icons";
import { useRootStore } from '../../../store/contexts';
import { WidgetRenderer } from './WidgetRenderer';
import { DashboardCustomizeDialog } from './DashboardCustomizeDialog';
import { AvailableWidget } from '../../store/DashboardStore';

export interface DashboardContainerProps {
  /** Espacio entre widgets */
  spacing?: number;
}

export const DashboardContainer: React.FC<DashboardContainerProps> = observer(
  ({ spacing = 2 }) => {
    const { dashboardStore, langStore } = useRootStore();
    const [customizeOpen, setCustomizeOpen] = useState(false);

    // Cargar widgets al montar. Pull-only: el server invalida cache
    // por evento (ver entityMutationsRedisBridge.ts); el cliente no
    // tiene timers ni recursos que limpiar al desmontar.
    useEffect(() => {
      dashboardStore.fetchWidgets();
    }, [dashboardStore]);

    // Estado de carga inicial
    if (dashboardStore.loading && dashboardStore.widgets.length === 0) {
      return (
        <Box sx={{ p: 2 }}>
          <Grid container spacing={spacing}>
            {[1, 2, 3].map((i) => (
              <Grid item key={i} xs={12} sm={6} md={4}>
                <Skeleton variant="rounded" height={280} />
              </Grid>
            ))}
          </Grid>
        </Box>
      );
    }

    // Error general
    if (dashboardStore.error && dashboardStore.widgets.length === 0) {
      return (
        <Box sx={{ p: 2 }}>
          <Alert severity="error">
            Error cargando widgets: {dashboardStore.error}
          </Alert>
        </Box>
      );
    }

    // Botón "Personalizar" (visible siempre que el tenant tenga widgets,
    // aunque el usuario los haya apagado todos — es su vía de re-encenderlos).
    const customizeBar = dashboardStore.widgets.length > 0 && (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button
          size="small"
          startIcon={<TuneIcon />}
          onClick={() => setCustomizeOpen(true)}
        >
          {langStore.dashboardCustomizeCommand}
        </Button>
        <DashboardCustomizeDialog
          open={customizeOpen}
          onClose={() => setCustomizeOpen(false)}
        />
      </Box>
    );

    // Sin widgets
    if (dashboardStore.enabledWidgets.length === 0) {
      return (
        <Box sx={{ p: 2 }}>
          {customizeBar}
          <Alert severity="info">No hay widgets disponibles para mostrar.</Alert>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {customizeBar}
        <Grid container spacing={spacing}>
          {dashboardStore.enabledWidgets.map((widget: AvailableWidget) => {
            // Usar minWidth del widget para calcular tamaño responsive
            // minWidth está en unidades de 12 columnas (ej: 4 = 33%)
            const minWidth = widget.minWidth || 4;

            // Mapear minWidth a breakpoints de Grid
            // xs: siempre 12 (100% en móvil)
            // sm: 6 o 12 según minWidth
            // md/lg: usar minWidth directamente
            const smSize = minWidth <= 4 ? 6 : 12;
            const mdSize = Math.min(minWidth, 12) as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

            return (
              <Grid
                item
                key={widget.widgetKey}
                xs={12}
                sm={smSize}
                md={mdSize}
                lg={mdSize}
              >
                <WidgetRenderer widget={widget} />
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  }
);

export default DashboardContainer;
